import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@organizacaox/lai-design-system";
import {
  AudioLines,
  Check,
  Mic,
  Pause,
  Phone,
  PhoneOff,
  Play,
  Sparkles,
  TrendingUp,
  UserPlus,
} from "lucide-react";

const scenes = ["calls", "leads", "insights"] as const;
type Scene = (typeof scenes)[number];

const card =
  "rounded-3xl border border-white/40 bg-background/90 p-6 text-foreground shadow-2xl shadow-black/30 backdrop-blur-xl";
const chip =
  "auth-float absolute flex items-center gap-2 rounded-2xl border border-white/30 bg-white/15 px-3.5 py-2.5 text-xs font-medium text-white shadow-lg shadow-black/20 backdrop-blur-md";

function Line({ width, className = "bg-muted" }: { width: string; className?: string }) {
  return <div className={`h-2 rounded-full ${className}`} style={{ width }} />;
}

function CardHeader({ preview, icon: Icon }: { preview: string; icon: typeof Phone }) {
  return (
    <div className="mb-6 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <img src="/logo.svg" alt="" className="size-7" />
        {preview}
      </div>
      <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="size-4" />
      </div>
    </div>
  );
}

/** Ligação em andamento: contato, onda de voz e controles. */
function CallsIllustration({ preview }: { preview: string }) {
  return (
    <>
      <div className={card}>
        <CardHeader preview={preview} icon={Phone} />
        <div className="flex items-center gap-4">
          <div className="relative">
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/30 motion-reduce:animate-none" />
            <div className="relative flex size-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 text-lg font-semibold text-primary-foreground">
              MC
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <Line width="45%" className="bg-foreground/15" />
            <Line width="28%" />
          </div>
          <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 font-mono text-xs font-medium text-emerald-600 dark:text-emerald-400">
            04:12
          </span>
        </div>
        <div className="my-6 flex h-20 items-center justify-center gap-1 rounded-2xl bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
          {Array.from({ length: 36 }, (_, i) => (
            <span
              key={i}
              className="auth-wave w-1 rounded-full bg-primary/70"
              style={{
                height: `${10 + ((i * 23) % 44)}px`,
                animationDelay: `${(i % 9) * -0.16}s`,
              }}
            />
          ))}
        </div>
        <div className="flex items-center justify-center gap-4">
          <div className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Mic className="size-5" />
          </div>
          <div className="flex size-12 items-center justify-center rounded-full bg-red-500 text-white shadow-lg shadow-red-500/30">
            <PhoneOff className="size-5" />
          </div>
          <div className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <AudioLines className="size-5" />
          </div>
        </div>
      </div>
      <div className={`${chip} right-0 top-0`}>
        <span className="size-2 rounded-full bg-emerald-400" />
        <AudioLines className="size-4" />
      </div>
      <div className={`${chip} left-0 bottom-2 [animation-delay:-2.5s]`}>
        <Sparkles className="size-4" />
        <Line width="4rem" className="bg-white/50" />
      </div>
    </>
  );
}

const leadRows = [
  { initials: "AS", tone: "from-sky-500 to-blue-600", width: "55%", stage: 3 },
  { initials: "RL", tone: "from-violet-500 to-indigo-600", width: "42%", stage: 2 },
  { initials: "JM", tone: "from-emerald-500 to-teal-600", width: "62%", stage: 4 },
  { initials: "CP", tone: "from-amber-500 to-orange-600", width: "36%", stage: 1 },
];

/** Carteira de leads com o avanço de cada um no funil. */
function LeadsIllustration({ preview }: { preview: string }) {
  return (
    <>
      <div className={card}>
        <CardHeader preview={preview} icon={UserPlus} />
        <div className="space-y-3">
          {leadRows.map((row) => (
            <div
              key={row.initials}
              className="flex items-center gap-3 rounded-2xl border bg-background/60 p-3"
            >
              <div
                className={`flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold text-white ${row.tone}`}
              >
                {row.initials}
              </div>
              <div className="flex-1 space-y-1.5">
                <Line width={row.width} className="bg-foreground/15" />
                <Line width="30%" />
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((step) => (
                  <span
                    key={step}
                    className={`h-1.5 w-5 rounded-full ${step <= row.stage ? "bg-primary" : "bg-muted"}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={`${chip} right-0 top-0`}>
        <UserPlus className="size-4" />
        +12
      </div>
      <div className={`${chip} left-0 bottom-0 [animation-delay:-2.5s]`}>
        <Check className="size-4" />
        <Line width="3.5rem" className="bg-white/50" />
      </div>
    </>
  );
}

const trend = [18, 26, 22, 34, 30, 42, 38, 50, 47, 58];

/** Avaliação de uma ligação: nota, evolução e pontos de melhoria. */
function InsightsIllustration({ preview }: { preview: string }) {
  const points = trend.map((value, i) => `${(i / (trend.length - 1)) * 200},${64 - value}`);
  const circumference = 2 * Math.PI * 30;
  return (
    <>
      <div className={card}>
        <CardHeader preview={preview} icon={Sparkles} />
        <div className="flex items-center gap-6">
          <div className="relative size-24 shrink-0">
            <svg viewBox="0 0 72 72" className="size-full -rotate-90">
              <circle cx="36" cy="36" r="30" fill="none" strokeWidth="7" className="stroke-muted" />
              <circle
                cx="36"
                cy="36"
                r="30"
                fill="none"
                strokeWidth="7"
                strokeLinecap="round"
                className="stroke-primary"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * 0.08}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold">
              92
            </span>
          </div>
          <svg viewBox="0 0 200 64" className="h-24 flex-1 overflow-visible">
            <defs>
              <linearGradient id="auth-trend" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={`0,64 ${points.join(" ")} 200,64`} fill="url(#auth-trend)" />
            <polyline
              points={points.join(" ")}
              fill="none"
              stroke="var(--primary)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="mt-6 space-y-2.5">
          {["70%", "56%", "64%"].map((width, i) => (
            <div key={width} className="flex items-center gap-3">
              <span
                className={`flex size-5 shrink-0 items-center justify-center rounded-full ${i < 2 ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-primary/10 text-primary"}`}
              >
                {i < 2 ? <Check className="size-3" /> : <Sparkles className="size-3" />}
              </span>
              <Line width={width} />
            </div>
          ))}
        </div>
      </div>
      <div className={`${chip} right-0 top-0`}>
        <TrendingUp className="size-4" />
        +18%
      </div>
      <div className={`${chip} left-0 bottom-1 [animation-delay:-2.5s]`}>
        <Sparkles className="size-4" />
        <Line width="4rem" className="bg-white/50" />
      </div>
    </>
  );
}

const illustrations: Record<Scene, (props: { preview: string }) => ReactNode> = {
  calls: CallsIllustration,
  leads: LeadsIllustration,
  insights: InsightsIllustration,
};

/** Apresentação ilustrativa do produto; não representa dados de uma conta. */
export function AuthShowcase() {
  const { t } = useTranslation();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!api) return;
    const select = () => setCurrent(api.selectedScrollSnap());
    api.on("select", select);
    return () => {
      api.off("select", select);
    };
  }, [api]);

  // O avanço automático vem do fim da animação da barra do ponto ativo: assim a
  // barra e a troca de cena nunca saem de compasso, pausar congela as duas, e
  // com movimento reduzido (animação desligada) não há avanço automático.
  const running = !paused && !hovered && !focused;

  return (
    <aside
      className="relative hidden min-w-0 flex-1 flex-col items-center justify-center gap-8 overflow-hidden bg-primary p-10 text-white lg:flex xl:p-12"
      aria-label={t("auth.showcase.label")}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <img src="/auth.jpg" alt="" className="size-full scale-110 object-cover blur-sm" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-transparent to-black/50" />
        <div className="absolute -left-32 top-1/4 size-[30rem] rounded-full bg-white/15 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 size-[26rem] rounded-full bg-black/30 blur-3xl" />
      </div>
      <Carousel
        setApi={setApi}
        opts={{ loop: true, align: "start" }}
        className="relative w-full max-w-2xl"
        aria-label={t("auth.showcase.label")}
        aria-live="off"
      >
        <CarouselContent>
          {scenes.map((scene, index) => {
            const Scene = illustrations[scene];
            return (
              <CarouselItem key={scene} aria-hidden={current !== index}>
                <div className="flex flex-col items-center gap-10">
                  <div aria-hidden="true" className="relative w-full px-8 pt-6 pb-8">
                    <Scene preview={t(`auth.showcase.${scene}.preview`)} />
                  </div>
                  <div className="max-w-md text-center">
                    <h2 className="text-2xl font-semibold tracking-tight drop-shadow-sm">
                      {t(`auth.showcase.${scene}.title`)}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-white/80">
                      {t(`auth.showcase.${scene}.description`)}
                    </p>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      <div className="relative flex items-center gap-1 rounded-full border border-white/20 bg-white/10 p-1 shadow-lg shadow-black/20 backdrop-blur-md">
        {scenes.map((scene, index) => {
          const active = current === index;
          return (
            <Button
              key={scene}
              type="button"
              variant="ghost"
              className="group h-8 rounded-full px-2 hover:bg-transparent dark:hover:bg-transparent"
              onClick={() => api?.scrollTo(index)}
              aria-label={t(`auth.showcase.${scene}.title`)}
              aria-current={active ? "true" : undefined}
            >
              <span
                className={`relative h-1.5 overflow-hidden rounded-full transition-[width,background-color] duration-500 ease-out motion-reduce:transition-none ${active ? "w-10 bg-white/30" : "w-1.5 bg-white/45 group-hover:bg-white/80"}`}
              >
                {active && (
                  <span
                    key={current}
                    className="auth-progress absolute inset-y-0 left-0 rounded-full bg-white"
                    style={{ animationPlayState: running ? "running" : "paused" }}
                    onAnimationEnd={() => api?.scrollNext()}
                  />
                )}
              </span>
            </Button>
          );
        })}
        <span aria-hidden="true" className="mx-1 h-4 w-px bg-white/25" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 rounded-full text-white/90 hover:bg-white/15 hover:text-white dark:hover:bg-white/15"
          onClick={() => setPaused(!paused)}
          aria-label={t(paused ? "auth.showcase.resume" : "auth.showcase.pause")}
        >
          {paused ? (
            <Play className="size-3.5 fill-current" />
          ) : (
            <Pause className="size-3.5 fill-current" />
          )}
        </Button>
      </div>
    </aside>
  );
}
